import { Component, computed, inject, signal } from '@angular/core';
import { environment } from '../../../../environments/environment.local';
import { CurrencyModel } from '../../../shared/models/currency.model';
import { ColumnConfig, FieldType, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalMode, ModalTabConfig, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateUtils } from '../../../shared/utils/date.utils';

@Component({
  selector: 'app-currency',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './currency.html'
})
export class Currency {

  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);

  endpoint = environment.API.CURRENCY_URI;
  currency = signal<CurrencyModel[]>([]);
  columns: ColumnConfig<CurrencyModel>[] = [
    {
      label: 'currency.code',
      key: 'code',
    },
    {
      label: 'currency.name',
      key: 'name',
    },
    {
      label: 'currency.symbol',
      key: 'symbol',
    },
    {
      label: 'currency.isActive',
      key: 'isActive',
      format: (value: string) => {
        if (value) {
          return this.translateService.instant(`common.yes`);
        } else {
          return this.translateService.instant(`common.no`);
        }
      }
    },
    {
      label: 'currency.createdAt',
      key: 'createdAt',
      sortable: true,
      format: (value: any) => DateUtils.format(value)
    },
  ];

  modalFields = computed<ModalTabConfig<CurrencyModel>[]>(() => [
    {
      tabId: 'general_data',
      tabLabel: 'currency.general_data',
      fields: [
        {
          label: 'currency.code',
          placeholder: 'currency.codePlaceholder',
          key: 'code',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required, Validators.minLength(3), Validators.maxLength(3)],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
            maxlength: this.translateService.instant('validators.maxLength', { maxLength: 3 }),
            minlength: this.translateService.instant('validators.minLength', { minLength: 3 })
          }
        },
        {
          label: 'currency.name',
          placeholder: 'currency.namePlaceholder',
          key: 'name',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'currency.symbol',
          placeholder: 'currency.symbolPlaceholder',
          key: 'symbol',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required, Validators.maxLength(10)],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
            maxlength: this.translateService.instant('validators.maxLength', { maxLength: 10 }),
          }
        },
        {
          label: 'currency.createdAt',
          key: 'createdAt',
          type: FieldType.DATE,
          readonly: true,
          modes: {
            [ModalMode.CREATE]: { visible: false, includeInPayload: false },
            [ModalMode.EDIT]: { visible: false, includeInPayload: false },
            [ModalMode.VIEW]: { visible: true, includeInPayload: false },
          },
        },
        {
          label: 'currency.isActive',
          placeholder: 'currency.isActivePlaceholder',
          key: 'isActive',
          type: FieldType.SWITCH,
          defaultValue: true,
          readonly: false,
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        }
      ],
    },
  ] as any as ModalTabConfig<CurrencyModel>[]);

  globalActions: GlobalAction<CurrencyModel>[] = [];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.SELECT, isExtra: false },
    { action: TableAction.CREATE, isExtra: false },
  ];

  portsModelFactory = (data: Partial<CurrencyModel>): CurrencyModel => {
    return new CurrencyModel().fromJSON(data);
  };

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }
}
